import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Button, Field, Input, Skeleton } from '@mgdlead/ui';

import { AuthLayout } from '../../components/AuthLayout';
import { FormAlert } from '@mgdlead/ui';
import { useSession } from '../../auth/useSession';
import { resendVerificationEmail, verifyEmail } from '../../lib/auth';
import { humanizeError } from '../../lib/errors';
import { resendVerificationSchema } from '../../lib/schemas';
import type { ResendVerificationValues } from '../../lib/schemas';
import { useDocumentTitle } from '../../lib/useDocumentTitle';
import { useZodForm } from '../../lib/useZodForm';

type VerifyState = 'idle' | 'verifying' | 'verified' | 'failed';

export function VerifyEmailPage() {
  useDocumentTitle('Verificar e-mail');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useSession();

  const token = params.get('token');
  const [state, setState] = useState<VerifyState>(token === null ? 'idle' : 'verifying');
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    if (token === null || attempted.current) return;
    // o React 19 monta o efeito duas vezes em dev; verificar 2x queima o token
    attempted.current = true;

    verifyEmail(token)
      .then(() => setState('verified'))
      .catch((cause: unknown) => {
        setError(humanizeError(cause));
        setState('failed');
      });
  }, [token]);

  const initialEmail: ResendVerificationValues = {
    email: params.get('email') ?? user?.email ?? '',
  };

  const onResend = useCallback(async (values: { email: string }) => {
    await resendVerificationEmail(values.email);
    setResent(true);
  }, []);

  const form = useZodForm({
    schema: resendVerificationSchema,
    initialValues: initialEmail,
    onSubmit: onResend,
  });

  if (state === 'verifying') {
    return (
      <AuthLayout title="Verificando..." description="Conferindo o link que você abriu.">
        <div role="status" aria-live="polite" aria-busy="true" className="flex flex-col gap-2">
          <span className="sr-only">Verificando seu e-mail</span>
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </AuthLayout>
    );
  }

  if (state === 'verified') {
    return (
      <AuthLayout title="E-mail confirmado" description="Sua conta está ativa.">
        <FormAlert tone="success">Pronto. Agora você já pode usar o painel.</FormAlert>
        <Button variant="dark" size="lg" block onClick={() => void navigate('/', { replace: true })}>
          Continuar
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={state === 'failed' ? 'Não conseguimos confirmar' : 'Confirme seu e-mail'}
      description={
        state === 'failed'
          ? 'O link pode ter expirado ou já ter sido usado. Peça um novo abaixo.'
          : 'Abra o link que enviamos para ativar a conta. Se ele não chegou, reenvie.'
      }
      footer={
        <Link
          to="/login"
          className="font-medium text-primary-text underline-offset-4 hover:underline"
        >
          Voltar para entrar
        </Link>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <FormAlert>{error}</FormAlert> : null}
        {resent ? (
          <FormAlert tone="success">
            Se houver uma conta pendente com esse e-mail, um novo link acabou de sair.
          </FormAlert>
        ) : null}

        <form noValidate onSubmit={form.handleSubmit} className="flex flex-col gap-4">
          {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}

          <Field label="E-mail da conta" error={form.errors.email} required>
            {(control) => (
              <Input
                {...control}
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                spellCheck={false}
                value={form.values.email}
                invalid={form.errors.email !== undefined}
                onChange={(event) => form.setField('email', event.target.value)}
              />
            )}
          </Field>

          <Button
            type="submit"
            variant="dark" size="lg"
            block
            loading={form.pending}
            loadingLabel="Reenviando"
          >
            Reenviar link
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
